import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CKEditorWithSource from "./CKEditorWithSource";
import config from "../coreFIles/config";
import {
  adminCourseGetAction,
  adminCourseOnboardingStepCreateAction,
  adminCourseOnboardingStepGetAction,
  adminCourseOnboardingStepUpdateAction,
  adminCourseStepSectionsListAction
} from "../Action/action";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

/** Lowercase extensions only (no leading dot). Used to reject non-video filenames in the browser. */
const VIDEO_FILE_EXTENSIONS = new Set(["mp4","webm","ogg","ogv","mov","qt","avi","mkv","m4v","3gp","3g2","flv","wmv","mpeg","mpg","mp2","m2v","ts","m2ts","mts","f4v","asf","vob"
]);

function getFileExtension(fileName) {
  if (!fileName || typeof fileName !== "string") return "";
  const base = fileName.split(/[/\\]/).pop() || "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}

function isAllowedVideoExtension(fileName) {
  return VIDEO_FILE_EXTENSIONS.has(getFileExtension(fileName));
}

/** Raster/vector image extensions allowed for icon & thumbnail uploads */
const IMAGE_FILE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "svg",
  "ico",
  "bmp",
  "avif"
]);

function isAllowedImageExtension(fileName) {
  return IMAGE_FILE_EXTENSIONS.has(getFileExtension(fileName));
}

/** Extension list + optional MIME check (some browsers omit or misreport type). */
function isAllowedImageFile(file) {
  if (!file || !file.name) return false;
  if (!isAllowedImageExtension(file.name)) return false;
  const t = String(file.type || "").trim();
  if (t && !t.toLowerCase().startsWith("image/")) return false;
  return true;
}

const STEP_TYPES = [
  { value: "video", label: "Video" },
  { value: "form", label: "Form" },
  { value: "question", label: "Question" },
  { value: "notification", label: "Notification" }
];

const VIDEO_ORIENTATION_OPTIONS = [
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" }
];

function normalizeStepType(v) {
  const s = String(v || "").toLowerCase();
  if (s === "form" || s === "question" || s === "notification") return s;
  return "video";
}

export default function CourseOnboardingStepForm({ mode }) {
  const { courseId, stepId } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [courseTitle, setCourseTitle] = useState("Course");

  const [stepNumber, setStepNumber] = useState(1);
  const [stepType, setStepType] = useState("video");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  /** Path or URL for video (same pattern as Icon URL); upload sets server path on save */
  const [videoUrl, setVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState("");
  const [videoOrientation, setVideoOrientation] = useState("landscape");
  const [videoDescription, setVideoDescription] = useState("");
  const [description, setDescription] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [thumbnailImage, setThumbnailImage] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [stepSectionOptions, setStepSectionOptions] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const iconInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function loadCourse() {
      if (!courseId) return;
      try {
        const res = await adminCourseGetAction(courseId);
        if (cancelled) return;
        if (res?.success && res?.data?.title) setCourseTitle(res.data.title);
      } catch (e) {}
    }
    loadCourse();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    let cancelled = false;
    async function loadSections() {
      setSectionsLoading(true);
      try {
        const res = await adminCourseStepSectionsListAction({
          active_only: 1,
          page: 1,
          per_page: 500
        });
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setStepSectionOptions(rows);
      } catch (e) {
        if (!cancelled) setStepSectionOptions([]);
      } finally {
        if (!cancelled) setSectionsLoading(false);
      }
    }
    loadSections();
    return () => {
      cancelled = true;
    };
  }, []);

  const sectionSelectOptions = useMemo(
    () =>
      stepSectionOptions.map((row) => {
        const id = Number(row.id);
        return {
          value: id,
          label:
            (row.title || `Section #${row.id}`) +
            (row.display_order != null ? ` (order ${row.display_order})` : "")
        };
      }),
    [stepSectionOptions]
  );

  const sectionSelectValue = useMemo(
    () => sectionSelectOptions.filter((o) => selectedSectionIds.includes(o.value)),
    [sectionSelectOptions, selectedSectionIds]
  );

  const sectionSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#2B7BCA" : "#D1D5DB",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(43, 123, 202, 0.12)" : "none",
        fontSize: "13.5px",
        "&:hover": {
          borderColor: state.isFocused ? "#2B7BCA" : "#9CA3AF"
        }
      }),
      menu: (base) => ({ ...base, zIndex: 1060 }),
      menuList: (base) => ({ ...base, maxHeight: 280 }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "rgba(43, 123, 202, 0.12)"
      }),
      multiValueLabel: (base) => ({ ...base, color: "#1E5A96", fontSize: "12.5px" }),
      multiValueRemove: (base) => ({
        ...base,
        ":hover": { backgroundColor: "rgba(43, 123, 202, 0.22)", color: "#1F2937" }
      }),
      placeholder: (base) => ({ ...base, color: "#6B7280", fontSize: "13.5px" })
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    async function loadStep() {
      if (!isEdit || !stepId) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseOnboardingStepGetAction(stepId);
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load step.");
        const s = res.data || {};
        const st = normalizeStepType(s.step_type);
        setStepNumber(Number(s.step_number || 1));
        setStepType(st);
        setTitle(s.title || "");
        setSubtitle(s.subtitle || "");
        const ids = Array.isArray(s.step_section_ids) ? s.step_section_ids.map((x) => Number(x)).filter((n) => n > 0) : [];
        setSelectedSectionIds(ids);
        if (st === "video") {
          setVideoUrl(s.video_url || "");
          setVideoDuration(s.video_duration || "");
          setVideoOrientation(s.video_orientation === "portrait" ? "portrait" : "landscape");
          setVideoDescription(s.video_description || "");
          setThumbnailImage(s.thumbnail_image || "");
        } else {
          setVideoUrl("");
          setVideoFile(null);
          setVideoDuration("");
          setVideoOrientation("landscape");
          setVideoDescription("");
          setThumbnailImage("");
          setThumbnailFile(null);
        }
        setDescription(s.description || "");
        setIconUrl(s.icon_url || "");
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load step.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadStep();
    return () => {
      cancelled = true;
    };
  }, [isEdit, stepId]);

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";

    const stepRaw = String(stepNumber ?? "").trim();
    if (!stepRaw) errs.stepNumber = "Step number is required.";
    else {
      const n = Number(stepRaw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
        errs.stepNumber = "Step number must be a whole number greater than 0.";
      }
    }

    if (stepType === "video") {
      const hasVideo = !!(videoFile || String(videoUrl || "").trim());
      if (!hasVideo) errs.videoFile = "Upload a video file or enter a video URL.";
      else if (videoFile && !isAllowedVideoExtension(videoFile.name)) {
        errs.videoFile =
          "File must use a video extension (e.g. .mp4, .webm, .mov, .mkv).";
      }
    }

    if (iconFile && !isAllowedImageFile(iconFile)) {
      errs.iconFile =
        "Icon must be an image file (e.g. .png, .jpg, .webp, .gif, .svg).";
    }
    if (stepType === "video" && thumbnailFile && !isAllowedImageFile(thumbnailFile)) {
      errs.thumbnailFile =
        "Thumbnail must be an image file (e.g. .png, .jpg, .webp, .gif, .svg).";
    }
    return errs;
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.warn("Please fix the highlighted fields.", { toastId: "onboarding-step-validate" });
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      const isVideo = stepType === "video";
      const isQuestion = stepType === "question";
      fd.append("step_number", String(Number(stepNumber)));
      fd.append("title", title.trim());
      fd.append("subtitle", subtitle.trim());
      fd.append("step_type", stepType);
      fd.append("video_duration", isVideo ? videoDuration.trim() : "");
      fd.append("video_orientation", isVideo ? videoOrientation : "");
      fd.append("video_description", isVideo ? videoDescription || "" : "");
      fd.append("description", description || "");
      fd.append("icon_url", iconUrl.trim());
      fd.append("thumbnail_image", isVideo ? thumbnailImage.trim() : "");
      fd.append("video_url", isVideo ? videoUrl.trim() : "");
      fd.append("step_section_ids", JSON.stringify(isQuestion ? selectedSectionIds : []));
      if (isVideo && videoFile) fd.append("video_file", videoFile);
      if (iconFile) fd.append("icon_file", iconFile);
      if (isVideo && thumbnailFile) fd.append("thumbnail_file", thumbnailFile);

      const res = isEdit
        ? await adminCourseOnboardingStepUpdateAction(stepId, fd)
        : await adminCourseOnboardingStepCreateAction(courseId, fd);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(`Step ${isEdit ? "updated" : "created"}`, { toastId: "onboarding-step-save" });
      navigate(`/courses/${courseId}/steps`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "onboarding-step-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save step.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb">
          <Link to="/courses">Courses</Link>
          <span>›</span>
          <Link to={`/courses/${courseId}/edit`}>{courseTitle}</Link>
          <span>›</span>
          <Link to={`/courses/${courseId}/steps`}>Onboarding Steps</Link>
          <span>›</span>
          <span>{isEdit ? "Edit Step" : "Add Step"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/courses/${courseId}/steps`} className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card mb-6">
            <h2 className="section-title">Step Details</h2>

            <div className="form-group">
              <label>
                Title <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...p, title: "" }));
                }}
                aria-invalid={!!fieldErrors.title}
                aria-describedby={fieldErrors.title ? "field-error-title" : undefined}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? (
                <div id="field-error-title" className="small-label" style={{ marginTop: 6, color: "var(--danger)" }} role="alert">
                  {fieldErrors.title}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>Subtitle</label>
              <input className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Step Number <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  step="1"
                  value={stepNumber}
                  onChange={(e) => {
                    setStepNumber(e.target.value);
                    setFieldErrors((p) => ({ ...p, stepNumber: "" }));
                  }}
                  aria-invalid={!!fieldErrors.stepNumber}
                  aria-describedby={fieldErrors.stepNumber ? "field-error-stepNumber" : undefined}
                  style={fieldErrors.stepNumber ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.stepNumber ? (
                  <div id="field-error-stepNumber" className="small-label" style={{ marginTop: 6, color: "var(--danger)" }} role="alert">
                    {fieldErrors.stepNumber}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>
                  Step type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={stepType}
                  onChange={(e) => {
                    const next = normalizeStepType(e.target.value);
                    setStepType(next);
                    setFieldErrors((p) => ({ ...p, videoFile: "", thumbnailFile: "" }));
                    if (next !== "video") {
                      setVideoUrl("");
                      setVideoFile(null);
                      setVideoDuration("");
                      setVideoOrientation("landscape");
                      setVideoDescription("");
                      setThumbnailImage("");
                      setThumbnailFile(null);
                      if (videoInputRef.current) videoInputRef.current.value = "";
                      if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
                    }
                    if (next !== "question") {
                      setSelectedSectionIds([]);
                    }
                  }}
                >
                  {STEP_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {stepType === "form" ? (
                  <p className="small-text" style={{ marginTop: 8, marginBottom: 0 }}>
                    Form steps use title, subtitle, and description. Video and thumbnail are not used.
                  </p>
                ) : null}
                {stepType === "notification" ? (
                  <p className="small-text" style={{ marginTop: 8, marginBottom: 0 }}>
                    Notification steps use title, subtitle, description, and icon. Video and thumbnail are not used.
                  </p>
                ) : null}
                {stepType === "question" ? (
                  <p className="small-text" style={{ marginTop: 8, marginBottom: 0 }}>
                    Question steps link to one or more active step sections (see Assets). Video fields are hidden.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                data={description}
                onChange={(html) => setDescription(html)}
              />
            </div>

            {stepType === "video" ? (
              <div className="form-group">
                <label>Video Description</label>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  data={videoDescription}
                  onChange={(html) => setVideoDescription(html)}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="section-title">Assets</h2>

            {stepType === "question" ? (
              <div className="form-group">
                <label htmlFor="step-sections-react-select">Step sections</label>
                
                <Select
                  inputId="step-sections-react-select"
                  instanceId="step-sections-react-select"
                  isMulti
                  isLoading={sectionsLoading}
                  isDisabled={sectionsLoading || sectionSelectOptions.length === 0}
                  options={sectionSelectOptions}
                  value={sectionSelectValue}
                  onChange={(selected) => {
                    setSelectedSectionIds(selected ? selected.map((s) => s.value) : []);
                  }}
                  placeholder={
                    sectionsLoading ? "Loading sections…" : "Choose step sections…"
                  }
                  noOptionsMessage={() => "No matching sections"}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={sectionSelectStyles}
                  classNamePrefix="rs-step-sections"
                />
                <div className="flex items-center gap-2" style={{ marginTop: 8, flexWrap: "wrap" }}>
                  
                </div>
                {!sectionsLoading && !stepSectionOptions.length ? (
                  <p className="small-text" style={{ marginTop: 8, color: "var(--warning, #b45309)" }}>
                    No active step sections found. Create and activate sections under Step Sections in the sidebar.
                  </p>
                ) : null}
              </div>
            ) : null}

            {stepType === "video" ? (
            <div className="form-row">
              <div className="form-group">
                  <label>Video Duration</label>
                  <input className="form-control" value={videoDuration} onChange={(e) => setVideoDuration(e.target.value)} placeholder="e.g. 05:30" />
                </div>
              <div className="form-group">
                <label>Video Orientation</label>
                <select className="form-control" value={videoOrientation} onChange={(e) => setVideoOrientation(e.target.value)}>
                  {VIDEO_ORIENTATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            ) : null}

            {stepType === "video" ? (
            <div className="form-group">
                  <label id="label-video-file">
                    Video URL <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={videoUrl}
                    onChange={(e) => {
                      setVideoUrl(e.target.value);
                      setFieldErrors((p) => ({ ...p, videoFile: "" }));
                    }}
                    placeholder="https://... or /uploads/videos/..."
                    aria-invalid={!!fieldErrors.videoFile}
                    aria-describedby={fieldErrors.videoFile ? "field-error-videoFile" : undefined}
                    style={fieldErrors.videoFile ? { borderColor: "var(--danger)" } : undefined}
                  />
                  <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
                    <button className="btn btn-outline btn-sm" type="button" onClick={() => videoInputRef.current?.click()} aria-labelledby="label-video-file">
                      Upload Video
                    </button>
                    {videoFile ? <span className="small-text">{videoFile.name}</span> : null}
                    {!videoFile && videoUrl.trim() ? <span className="small-text">URL or path above</span> : null}
                  </div>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*,.mp4,.webm,.mov,.mkv,.avi,.m4v,.ogg,.ogv"
                    style={{ display: "none" }}
                    aria-invalid={!!fieldErrors.videoFile}
                    aria-describedby={fieldErrors.videoFile ? "field-error-videoFile" : undefined}
                    onChange={(e) => {
                      const input = e.target;
                      const f = input.files && input.files[0] ? input.files[0] : null;
                      if (f && !isAllowedVideoExtension(f.name)) {
                        setVideoFile(null);
                        setFieldErrors((p) => ({
                          ...p,
                          videoFile:
                            "Only video file extensions are allowed (e.g. .mp4, .webm, .mov, .mkv)."
                        }));
                        input.value = "";
                        return;
                      }
                      setVideoFile(f);
                      setFieldErrors((p) => ({ ...p, videoFile: "" }));
                    }}
                  />
                  {fieldErrors.videoFile ? (
                    <div id="field-error-videoFile" className="small-label" style={{ marginTop: 6, color: "var(--danger)" }} role="alert">
                      {fieldErrors.videoFile}
                    </div>
                  ) : null}
                  {videoUrl.trim() || videoFile ? (
                    <div className="small-text" style={{ marginTop: 10, lineHeight: 1.5 }}>
                      {videoUrl.trim() ? (
                        <div style={{ wordBreak: "break-all" }}>
                          <span className="small-label" style={{ display: "block", marginBottom: 4 }}>
                            Video URL
                          </span>
                          <a
                            href={resolvePublicUrl(videoUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="action-link"
                          >
                            View video
                          </a>
                        </div>
                      ) : null}
                      {videoFile ? (
                        <div style={{ marginTop: videoUrl.trim() ? 8 : 0, wordBreak: "break-all" }}>
                          <span className="small-label" style={{ display: "block", marginBottom: 4 }}>
                            {videoUrl.trim() ? "New file (on save)" : "Pending upload"}
                          </span>
                          <code>{videoFile.name}</code>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
            ) : null}

            <div className="form-group">
              <label>Icon URL</label>
              <input
                className="form-control"
                value={iconUrl}
                onChange={(e) => {
                  setIconUrl(e.target.value);
                  setFieldErrors((p) => ({ ...p, iconFile: "" }));
                }}
                placeholder="https://... or /uploads/images/..."
                aria-invalid={!!fieldErrors.iconFile}
                aria-describedby={fieldErrors.iconFile ? "field-error-iconFile" : undefined}
                style={fieldErrors.iconFile ? { borderColor: "var(--danger)" } : undefined}
              />
              <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
                <button className="btn btn-outline btn-sm" type="button" onClick={() => iconInputRef.current?.click()}>
                  Upload Icon
                </button>
                {iconFile ? <span className="small-text">{iconFile.name}</span> : null}
              </div>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.svg,.ico,.bmp,.avif"
                style={{ display: "none" }}
                aria-invalid={!!fieldErrors.iconFile}
                aria-describedby={fieldErrors.iconFile ? "field-error-iconFile" : undefined}
                onChange={(e) => {
                  const input = e.target;
                  const f = input.files && input.files[0] ? input.files[0] : null;
                  if (f && !isAllowedImageFile(f)) {
                    setIconFile(null);
                    setFieldErrors((p) => ({
                      ...p,
                      iconFile:
                        "Only valid image files are allowed (e.g. .png, .jpg, .webp, .gif, .svg)."
                    }));
                    input.value = "";
                    return;
                  }
                  setIconFile(f);
                  setFieldErrors((p) => ({ ...p, iconFile: "" }));
                }}
              />
              {fieldErrors.iconFile ? (
                <div id="field-error-iconFile" className="small-label" style={{ marginTop: 6, color: "var(--danger)" }} role="alert">
                  {fieldErrors.iconFile}
                </div>
              ) : null}
              {iconUrl ? (
                <img src={resolvePublicUrl(iconUrl)} alt="Icon" style={{ width: 48, height: 48, objectFit: "cover", marginTop: 10, borderRadius: 8 }} />
              ) : null}
            </div>

            {stepType === "video" ? (
            <div className="form-group">
              <label>Thumbnail URL</label>
              <input
                className="form-control"
                value={thumbnailImage}
                onChange={(e) => {
                  setThumbnailImage(e.target.value);
                  setFieldErrors((p) => ({ ...p, thumbnailFile: "" }));
                }}
                placeholder="https://... or /uploads/images/..."
                aria-invalid={!!fieldErrors.thumbnailFile}
                aria-describedby={fieldErrors.thumbnailFile ? "field-error-thumbnailFile" : undefined}
                style={fieldErrors.thumbnailFile ? { borderColor: "var(--danger)" } : undefined}
              />
              <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
                <button className="btn btn-outline btn-sm" type="button" onClick={() => thumbnailInputRef.current?.click()}>
                  Upload Thumbnail
                </button>
                {thumbnailFile ? <span className="small-text">{thumbnailFile.name}</span> : null}
              </div>
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.svg,.ico,.bmp,.avif"
                style={{ display: "none" }}
                aria-invalid={!!fieldErrors.thumbnailFile}
                aria-describedby={fieldErrors.thumbnailFile ? "field-error-thumbnailFile" : undefined}
                onChange={(e) => {
                  const input = e.target;
                  const f = input.files && input.files[0] ? input.files[0] : null;
                  if (f && !isAllowedImageFile(f)) {
                    setThumbnailFile(null);
                    setFieldErrors((p) => ({
                      ...p,
                      thumbnailFile:
                        "Only valid image files are allowed (e.g. .png, .jpg, .webp, .gif, .svg)."
                    }));
                    input.value = "";
                    return;
                  }
                  setThumbnailFile(f);
                  setFieldErrors((p) => ({ ...p, thumbnailFile: "" }));
                }}
              />
              {fieldErrors.thumbnailFile ? (
                <div id="field-error-thumbnailFile" className="small-label" style={{ marginTop: 6, color: "var(--danger)" }} role="alert">
                  {fieldErrors.thumbnailFile}
                </div>
              ) : null}
              {thumbnailImage ? (
                <img src={resolvePublicUrl(thumbnailImage)} alt="Thumbnail" style={{ width: "100%", maxWidth: 100, marginTop: 10, borderRadius: 8 }} />
              ) : null}
            </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={onSave} disabled={saving || loading}>
          {saving ? "Saving..." : "Save Step"}
        </button>
        <Link to={`/courses/${courseId}/steps`} className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
